// Single source of truth for route classification. Owns which routes are
// public/indexable and which may carry analytics. The SEO phase reuses this
// registry for metadata, sitemap, and robots generation — add new routes HERE
// when adding them to App.tsx (a contract test cross-checks the two files).
import { isDscrPath, isDscrPublished } from '../../features/dscr-pages/publication';
import { LEARN_CATALOG } from '../../features/learn/catalog';
import { LEARN_INDEX_PATH, isLearnPath, isLearnPublished, learnArticlePath } from '../../features/learn/publication';

export interface RouteEntry {
  path: string;
  /** May GTM/GA see this route at all (page_views, events)? */
  analyticsEligible: boolean;
  /** May search engines index this route? */
  indexable: boolean;
  /** Does the Vite production build emit route-specific HTML for this path? */
  prerender: boolean;
  /** Emit an empty noindex SPA shell for a direct-entry internal app. */
  internalShell?: boolean;
  title: string;
  description: string;
  /** Override only when the public canonical URL differs from `path`. */
  canonicalPath?: string;
  /**
   * ISO date of the last substantive content change, emitted as the sitemap
   * `<lastmod>`. Only Learn articles set it, from their catalog, so no route
   * ever carries an invented modification date.
   */
  lastModified?: string;
}

export const SITE_ORIGIN = 'https://lendwisemtg.com';

// These entries are already approved for public indexing and prerendering.
// Gated, private and canonical-override entries stay explicit below.
function publishedRoute(path: string, title: string, description: string): RouteEntry {
  return { path, analyticsEligible: true, indexable: true, prerender: true, title, description };
}

export const PUBLIC_ROUTES: readonly RouteEntry[] = [
  publishedRoute(
    '/',
    'Mortgage Loans & Home Financing | Lendwise Mortgage',
    'Explore home purchase, refinance, conventional, FHA, VA, and non-QM mortgage options with guidance from Lendwise Mortgage loan officers.'
  ),
  publishedRoute(
    '/buy/first-time',
    'First-Time Home Buyer Guide | Lendwise Mortgage',
    'Learn the homebuying process, financing options, and practical steps for purchasing your first home with Lendwise Mortgage.'
  ),
  publishedRoute(
    '/buy/pre-approval',
    'Mortgage Pre-Approval: How It Works | Lendwise Mortgage',
    'Learn how mortgage pre-approval works, what lenders review and what your letter means. Lendwise READY adds an underwriter review before you find a home.'
  ),
  publishedRoute(
    '/buy/guide',
    'Home Buying Guide | Lendwise Mortgage',
    'Follow a clear homebuying guide from preparation and house hunting through mortgage application, underwriting, and closing.'
  ),
  publishedRoute(
    '/loans/conventional',
    'California Conventional Loans: 2026 Guide | Lendwise',
    'Compare 2026 California conventional loan limits, down-payment paths, credit rules, PMI cancellation, gifts, and seller contributions.'
  ),
  publishedRoute(
    '/loans/fha',
    'California FHA Loans: 2026 Requirements | Lendwise',
    'Learn 2026 California FHA requirements, county loan limits, credit and down-payment rules, mortgage insurance, closing costs, and special situations.'
  ),
  publishedRoute(
    '/loans/fha-vs-conventional',
    'FHA vs. Conventional Loans: Compare Your Options | Lendwise',
    'Compare FHA and conventional loan requirements, mortgage insurance, 2026 county limits, down payments, occupancy, and decision factors in California.'
  ),
  // Specialty educational cluster. The three routes launch as one linked
  // system so the parent chooser, both child guides, navigation, sitemap, and
  // prerendered artifacts cannot describe different publication states.
  publishedRoute(
    '/loans/non-qm',
    'Non-QM Loans: Programs, Rates & Requirements | Lendwise',
    'Understand Non-QM loans, ability-to-repay boundaries, Full Documentation, Bank Statement and DSCR paths, pricing factors, trade-offs, and next steps.'
  ),
  publishedRoute(
    '/loans/bank-statement',
    'Bank Statement Loans for Self-Employed | Lendwise',
    'Learn how Bank Statement loans review personal or business deposits, transfers, expenses, documentation, pricing factors, and conventional alternatives.'
  ),
  publishedRoute(
    '/loans/dscr',
    'DSCR Loans: Requirements, Calculator & Costs | Lendwise',
    'Learn what a DSCR loan is, how to calculate your ratio and what lenders review. Explore down payments, refinancing and how DSCR can affect rates.'
  ),
  {
    path: '/mortgage-rates/dscr',
    analyticsEligible: isDscrPublished(),
    indexable: isDscrPublished(),
    prerender: isDscrPublished(),
    title: 'DSCR Loan Rates & Quotes for Rental Property | Lendwise',
    description: 'Compare DSCR purchase, rate-and-term and cash-out financing for rental property. Review representative rates, assumptions and a private quote with Lendwise.'
  },
  {
    path: '/mortgage-rates/dscr/california',
    analyticsEligible: isDscrPublished('california'),
    indexable: isDscrPublished('california'),
    prerender: isDscrPublished('california'),
    title: 'California DSCR Loan Rates & Rental Quotes | Lendwise',
    description: 'Explore California DSCR rental-property financing. Review purchase and refinance options, reassessment considerations, rate assumptions and a private quote.'
  },
  {
    path: '/mortgage-rates/dscr/ohio',
    analyticsEligible: isDscrPublished('ohio'),
    indexable: isDscrPublished('ohio'),
    prerender: isDscrPublished('ohio'),
    title: 'Ohio DSCR Loan Rates & Rental Property Quotes | Lendwise',
    description: 'Explore Ohio DSCR rental-property financing. Review purchase and refinance options, county tax records, rate assumptions and your private quote options.'
  },
  {
    path: '/mortgage-rates/dscr/florida',
    analyticsEligible: isDscrPublished('florida'),
    indexable: isDscrPublished('florida'),
    prerender: isDscrPublished('florida'),
    title: 'Florida DSCR Loan Rates & Rental Property Quotes | Lendwise',
    description: 'Explore Florida DSCR rental-property financing. Review purchase and refinance options, insurance and tax considerations, rate assumptions and a private quote.'
  },
  {
    path: '/mortgage-rates/dscr/texas',
    analyticsEligible: isDscrPublished('texas'),
    indexable: isDscrPublished('texas'),
    prerender: isDscrPublished('texas'),
    title: 'Texas DSCR Loan Rates & Rental Property Quotes | Lendwise',
    description: 'Explore Texas DSCR rental-property financing. Review purchase and refinance options, local taxes, business-purpose requirements and private quote options.'
  },
  {
    path: '/mortgage-rates/dscr/tennessee',
    analyticsEligible: isDscrPublished('tennessee'),
    indexable: isDscrPublished('tennessee'),
    prerender: isDscrPublished('tennessee'),
    title: 'Tennessee DSCR Loan Rates & Rental Quotes | Lendwise',
    description: 'Explore Tennessee DSCR rental-property financing. Review purchase and refinance options, county and city tax considerations, assumptions and a private quote.'
  },
  publishedRoute(
    '/refinance/guide',
    'Mortgage Refinance Guide | Lendwise Mortgage',
    'Learn when refinancing may make sense, how the process works, and which costs and goals to evaluate before applying.'
  ),
  publishedRoute(
    '/refinance/loans',
    'Mortgage Refinance Loan Options | Lendwise Mortgage',
    'Explore rate-and-term and cash-out refinance options, common qualification factors, and next steps with Lendwise Mortgage.'
  ),
  publishedRoute(
    '/mortgage-rates',
    'California Mortgage Rates & Rate Quotes | Lendwise',
    'Compare representative California purchase rates, APR and points for conventional, FHA, bank statement and DSCR loans. Explore options for your scenario.'
  ),
  publishedRoute(
    '/resources/mortgage-rates',
    'Mortgage Rate Trends & Market Data | Lendwise Mortgage',
    'Review mortgage rate trends, Treasury data, and educational market context. Personalized mortgage pricing requires a complete scenario.'
  ),
  publishedRoute(
    '/resources/mortgage-news',
    'Mortgage News & Market Insights | Lendwise Mortgage',
    'Read Lendwise mortgage-market explainers alongside recent third-party reporting on rates, housing, and lending policy.'
  ),
  publishedRoute(
    '/loan-officers',
    'Find a Mortgage Loan Officer | Lendwise Mortgage',
    'Browse Lendwise Mortgage loan officers by name, specialty, and licensed state. Review NMLS IDs, profiles, and available contact details.'
  ),
  publishedRoute(
    '/calculators',
    'Mortgage Calculator: Estimate a Monthly Payment | Lendwise',
    'Estimate mortgage payments with taxes and insurance, compare loan terms, and see how extra principal changes interest and payoff time.'
  ),
  publishedRoute(
    '/calculators/affordability',
    'How Much House Can I Afford? Calculator | Lendwise',
    'Adjust your monthly housing budget and see an estimated home price and total DTI, including taxes, insurance, HOA dues, and mortgage insurance.'
  ),
  publishedRoute(
    '/calculators/refinance',
    'Refinance Calculator: Will a New Loan Save Money? | Lendwise',
    'Compare your current mortgage with a proposed loan: monthly difference, break-even point, remaining interest, and total cost before you refinance.'
  ),
  // Educational calculators approved for publication on October 20, 2026.
  publishedRoute(
    '/calculators/buydown',
    'Buydown Calculator: 2-1, 3-2-1 and 1-0 Payments | Lendwise',
    "See each year's payment under a 2-1, 3-2-1 or 1-0 temporary buydown, what the seller credit costs, and how it compares with a price cut or points."
  ),
  publishedRoute(
    '/calculators/discount-points',
    'Mortgage Points Calculator: Are Points Worth It? | Lendwise',
    'Compare paying discount points, no points or a lender credit: monthly savings, break-even time, and where you stand after the years you keep the loan.'
  ),
  publishedRoute(
    '/calculators/dscr',
    'DSCR Calculator: Rental Property Coverage | Lendwise',
    'Calculate rental property DSCR with rent, loan payments, taxes and insurance. Explore the rent or loan amount needed to reach your planning target.'
  ),
  publishedRoute(
    '/resources',
    'Mortgage Resources & Guides | Lendwise Mortgage',
    'Browse mortgage guides, calculators, rate context, and home financing resources from Lendwise Mortgage.'
  ),
  publishedRoute(
    '/technology',
    'Mortgage Technology for Loan Officers | Lendwise',
    'Explore the loan origination, pricing, CRM, and AI tools available to Lendwise loan officers and how each supports the mortgage workflow.'
  ),
  publishedRoute(
    '/why-lendwise',
    'Why Lendwise Mortgage | Licensed Guidance & Technology',
    'Learn how Lendwise Mortgage combines licensed loan-officer guidance with technology-supported application, pricing, and document workflows.'
  ),
  publishedRoute(
    '/join',
    'Mortgage Loan Officer Jobs & Careers | Lendwise Mortgage',
    'Mortgage loan officer careers at Lendwise: keep 100% of your commission on loans we fund, no monthly tech fees, and same-day in-house underwriting.'
  ),
  publishedRoute(
    '/join/marketing',
    'Loan Officer Marketing Support | Lendwise Mortgage',
    "Work one-on-one with Lendwise's marketing director, film crew, and on-camera coach: weekly scripts in your voice, filming, editing, and compliance review."
  ),
  // Learn library: registered from the first commit and held until the cohort
  // gate in features/learn/publication.ts opens; see
  // docs/seo/decisions/2026-11-01-learn-library-adoption.md.
  {
    path: LEARN_INDEX_PATH,
    analyticsEligible: isLearnPublished(),
    indexable: isLearnPublished(),
    prerender: isLearnPublished(),
    title: 'Learn: Mortgage Questions Answered Plainly | Lendwise',
    description: 'Plain answers to the questions people ask about DSCR and rental property loans, written by Lendwise and checked by underwriting before they publish.'
  },
  ...LEARN_CATALOG.map((entry): RouteEntry => ({
    path: learnArticlePath(entry.slug),
    analyticsEligible: isLearnPublished(entry.slug),
    indexable: isLearnPublished(entry.slug),
    prerender: isLearnPublished(entry.slug),
    title: entry.title,
    description: entry.description,
    ...(entry.updatedOn ? { lastModified: entry.updatedOn } : {})
  })),
  // Private personalized flow; an explicit live build flag controls publication.
  // Only ?view=unsubscribe can load the separate cancellation-only shell.
  {
    path: '/rate-experience',
    analyticsEligible: false,
    indexable: false,
    prerender: false,
    title: 'Your Mortgage Rate Options | Lendwise Mortgage',
    description: 'Compare mortgage options for your loan scenario with Lendwise Mortgage.'
  },
  // Canonical compliance document is the static privacy-policy.html; the SPA
  // route only redirects to it. Not an analytics surface.
  {
    path: '/privacy-policy',
    analyticsEligible: false,
    indexable: true,
    prerender: false,
    title: 'Privacy Policy | Lendwise Mortgage',
    description: 'Lendwise Mortgage privacy policy, SMS privacy terms, and mobile messaging disclosures.'
  },
  // Internal/auth surfaces — NEVER analytics-eligible. They are reached by
  // full-document navigation so an armed public document can't carry GTM in.
  {
    path: '/orion',
    analyticsEligible: false,
    indexable: false,
    prerender: false,
    internalShell: true,
    title: 'Orion',
    description: 'Internal Lendwise Mortgage investor intelligence.'
  },
  {
    path: '/recruit-login',
    analyticsEligible: false,
    indexable: false,
    prerender: false,
    internalShell: true,
    title: 'Recruit Login',
    description: 'Secure Lendwise Mortgage recruiting portal.'
  },
  // Private LO-authored quote delivery. Real pages live at /rate-quote/<token>
  // and are served this shell; the bare path exists only so the registry, the
  // shell artifact, and the response headers have one owner. Never indexable,
  // never prerendered, never analytics-eligible.
  {
    path: '/rate-quote',
    analyticsEligible: false,
    indexable: false,
    prerender: false,
    internalShell: true,
    title: 'Your Rate Quote',
    description: 'Private Lendwise Mortgage rate quote prepared by a loan officer.'
  },
  // Authenticated, in-memory pre-send preview. It inherits the same private
  // response-header and paper-shell boundary as delivered quote links, but
  // has its own static route so it cannot be mistaken for a capability token.
  {
    path: '/rate-quote/preview',
    analyticsEligible: false,
    indexable: false,
    prerender: false,
    internalShell: true,
    title: 'Rate Quote Preview | Lendwise Mortgage',
    description: 'Private pre-send preview of a Lendwise Mortgage rate quote.'
  }
];

const eligible = new Set(
  PUBLIC_ROUTES.filter((r) => r.analyticsEligible).map((r) => r.path)
);

function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

/**
 * Dynamic per-officer profile pages. Slug shape mirrors the profile RPC's
 * own gate. These routes are prerendered by the fan-out (one HTML document
 * per published officer) with PER-PAGE metadata and indexability, so they
 * have no static registry entries — entries are built at prerender time by
 * loanOfficerRouteEntry() in ./loanOfficerRoutes.ts, kept out of this module
 * so the startup shell does not carry code only the fan-out and the lazy
 * profile head manager use.
 */
const LOAN_OFFICER_PROFILE_PATH = /^\/loan-officers\/[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;

export function isLoanOfficerProfilePath(pathname: string): boolean {
  return LOAN_OFFICER_PROFILE_PATH.test(normalizePath(pathname));
}

/**
 * The other dynamic route class: private, LO-authored rate-quote delivery
 * pages at /rate-quote/<token>. The 43-character token shape mirrors the
 * portal's capability token and the BFF's own gate. These paths deliberately
 * have NO registry entry of their own — they inherit the private '/rate-quote'
 * entry — so they can never become analytics-eligible or enter a crawl
 * artifact, and a token can never end up in a title, canonical, or event.
 */
const RATE_QUOTE_TOKEN_PATH = /^\/rate-quote\/[A-Za-z0-9_-]{43}$/;

export function isRateQuotePath(pathname: string): boolean {
  return RATE_QUOTE_TOKEN_PATH.test(normalizePath(pathname));
}

export function getPublicRoute(pathname: string): RouteEntry | undefined {
  const path = normalizePath(pathname);
  return PUBLIC_ROUTES.find((route) => route.path === path);
}

// Vite folds this to `false` in a production build; it is undefined outside a
// Vite context (node test runners), so it reads false there too.
const DEV_PREVIEW = typeof import.meta.env !== 'undefined' && import.meta.env.DEV === true;

/**
 * Should this destination be offered to someone using the site?
 *
 * The single predicate behind held routes: it decides whether App.tsx mounts
 * the route AND whether navigation, the resource library, site search, and
 * contextual prose offer a link to it. Held pages stay browsable locally for
 * review; production follows the registry.
 */
export function isRouteBrowsable(pathname: string): boolean {
  return DEV_PREVIEW || !isHeldRoute(pathname);
}

/**
 * Registered and built, but deliberately unpublished.
 *
 * A held path renders a genuine 404 client-side, so every surface reading the
 * registry has to agree: it must contribute no title, canonical, or program
 * schema to that 404. Derived rather than listed — a program route that is not
 * indexable is by definition not published — so flipping `indexable` on launch
 * day retires the whole set at once, with no second switch to forget.
 * Route and SEO contract tests pin this behavior whenever a future loan page
 * enters the held state.
 */
export function isHeldRoute(pathname: string): boolean {
  return (pathname.startsWith('/loans/') || pathname.startsWith('/calculators/') || isDscrPath(pathname) || isLearnPath(pathname)) && !isPubliclyLive(pathname);
}

/**
 * Is this path published to the public site right now?
 *
 * Navigation, resource listings, search, and contextual prose use this so a
 * held route disappears from the link graph instead of pointing at a 404. The
 * hold is then a single switch: flipping `indexable` back on restores every
 * link automatically, with no content churn to reverse at launch.
 */
export function isPubliclyLive(pathname: string): boolean {
  return getPublicRoute(pathname)?.indexable === true;
}

export function canonicalUrl(route: RouteEntry): string {
  return new URL(route.canonicalPath ?? route.path, SITE_ORIGIN).toString();
}

/**
 * A route may carry analytics only when EXPLICITLY registered as eligible.
 * Unknown routes fail closed and render the client-side noindex 404 without
 * emitting analytics. Netlify returns the static 404 for direct requests.
 * Officer profile pages are public marketing surfaces and are eligible as a
 * class (still subject to opt-out and Global Privacy Control like everything else).
 */
export function isAnalyticsEligible(pathname: string): boolean {
  return eligible.has(normalizePath(pathname)) || isLoanOfficerProfilePath(pathname);
}
